import { QueryClient } from '@tanstack/react-query'

// Create a query client with optimized configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time - data is immediately stale, will refetch on mount
      staleTime: 0,
      
      // Cache time - how long to keep data in cache when not in use (5 minutes)
      gcTime: 5 * 60 * 1000,
      
      // Retry configuration - don't retry on client errors (4xx) or server errors (5xx)
      // Only retry on network errors
      retry: (failureCount, error: any) => {
        // Don't retry on any HTTP error response (4xx or 5xx)
        if (error?.statusCode || error?.response?.status) {
          return false
        }
        // Only retry network errors (no response from server) up to 2 times
        return failureCount < 2
      },
      
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Always refetch on mount to get latest data when navigating
      refetchOnMount: 'always',
      
      // Disable auto-refetch on window focus for better performance
      refetchOnWindowFocus: false,
      
      // Refetch on network reconnect
      refetchOnReconnect: true,
    },
    mutations: {
      // Don't retry mutations - they should be explicit user actions
      retry: false,
      
      // Global error handling for mutations
      onError: (error: any) => {
        console.error('Mutation error:', error)
        // You can add toast notifications here
      },
    },
  },
})

// Query key factories for organized key management
export const queryKeys = {
  // Auth queries
  auth: {
    user: () => ['auth', 'user'] as const,
    profile: () => ['auth', 'profile'] as const,
  },
  
  // Dashboard queries
  dashboard: {
    overview: () => ['dashboard', 'overview'] as const,
    activities: () => ['dashboard', 'activities'] as const,
    inventory: () => ['dashboard', 'inventory'] as const,
    spaceUtilization: () => ['dashboard', 'space-utilization'] as const,
  },
  
  // Client queries
  clients: {
    all: () => ['clients'] as const,
    list: (filters?: any) => ['clients', 'list', { filters }] as const,
    detail: (id: string) => ['clients', 'detail', id] as const,
    spaceDistribution: () => ['clients', 'space-distribution'] as const,
  },
  
  // Product queries
  products: {
    all: () => ['products'] as const,
    list: (filters?: any) => ['products', 'list', { filters }] as const,
    detail: (id: string) => ['products', 'detail', id] as const,
    inventory: () => ['products', 'inventory'] as const,
  },
  
  // Warehouse queries
  warehouse: {
    overview: () => ['warehouse', 'overview'] as const,
    locations: () => ['warehouse', 'locations'] as const,
    utilization: () => ['warehouse', 'utilization'] as const,
  },
  
  // Billing config queries
  billingConfigs: {
    all: () => ['billing-configs'] as const,
    active: (clientId: number) => ['billing-configs', 'active', clientId] as const,
    history: (clientId: number) => ['billing-configs', 'history', clientId] as const,
  },

  // Invoice branding (tenant-wide company header config)
  invoiceBranding: {
    current: () => ['invoice-branding', 'current'] as const,
  },

  // Billing invoice queries
  billing: {
    all: () => ['billing'] as const,
    invoices: (clientId?: number) => ['billing', 'invoices', { clientId }] as const,
    invoice: (id: number) => ['billing', 'invoice', id] as const,
  },

  // Inventory queries
  inventory: {
    all: () => ['inventory'] as const,
    list: (filters?: any) => ['inventory', 'list', { filters }] as const,
    client: (clientId: number, filters?: any) => ['inventory', 'client', clientId, { filters }] as const,
    storage: (filters?: any) => ['inventory-summary', 'storage', { filters }] as const,
    transactions: (filters?: any) => ['inventory-transactions', { filters }] as const,
  },
}